import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useCurrentEditor } from '@tiptap/react';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import LocalActivityRoundedIcon from '@mui/icons-material/LocalActivityRounded';
import MenuList from '@components/MenuList';
import Tooltip from '@components/Tooltip';
import { couponsActions } from '@actions';
import { useTranslation, useCouponsTemplates, usePlaceholderResolver } from '@hooks';
import { serializePlaceholdersToHtml } from '@helpers/text-editor';
import { PLACEHOLDER_CONTEXT } from '@constants/placeholders';

const CouponsMenu = ({ disabled, setMessage, context }) => {
    const { t } = useTranslation();
    const { editor } = useCurrentEditor();
    const placeholderResolver = usePlaceholderResolver(PLACEHOLDER_CONTEXT.INBOX);
    const isMobile = useMediaQuery('@media (max-width: 600px)');
    const dispatch = useDispatch();

    const [anchorElMain, setAnchorElMain] = useState(null);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

    const companies = useSelector(state => state.account?.account?.companies || []);
    const couponsAll = useSelector(state => state.coupons.dataAll);
    const { getCouponsOptionsByLocationsId, getCouponsOptionsByBrandId } = useCouponsTemplates();

    useEffect(() => {
        if (couponsAll.length === 0) {
            dispatch(couponsActions.getAll());
        }
    }, [couponsAll.length]);

    const handleOpenMain = event => {
        if (isMobile) {
            setMobileMenuOpen(true);
        } else {
            setAnchorElMain(event.currentTarget);
        }
    };

    const handleCloseMain = () => setAnchorElMain(null);

    const onClickCoupon = coupon => {
        if (!editor) {
            return;
        }
        const endPosition = editor.state.doc.content.size;
        const locationName = context?.locationId
            ? companies.find(c => c.companyId === context.locationId)?.locationName || ''
            : companies.find(c => c.brand?.id === context?.brandId)?.brand?.title || '';

        let newMessage = '';
        if (coupon.defaultMessage) {
            newMessage = coupon.defaultMessage
                ?.replace(/\[name\]/g, context?.firstName || '')
                ?.replace(/\[last_name\]/g, context?.lastName || '')
                ?.replace(/\[location\]/g, locationName);
        }
        const placeholderWithId = `[coupon:${coupon.id}]`;
        const messageForEditor = `${newMessage ? `${newMessage} ` : ''}${placeholderWithId}`;
        // INFO: editor has paragraph tags, so we need to subtract 2
        if (endPosition - 2 === 0 || coupon.defaultMessage?.length > 0) {
            editor.commands.setContent(serializePlaceholdersToHtml(messageForEditor, placeholderResolver), {
                emitUpdate: false,
            });
        } else {
            editor
                .chain()
                .focus('end')
                .insertContent(newMessage ? ` ${newMessage} ` : ' ')
                .insertContent(serializePlaceholdersToHtml(placeholderWithId, placeholderResolver))
                .run();
        }
        setMessage(JSON.stringify(editor.getJSON()));
        setMobileMenuOpen(false);
        setAnchorElMain(null);
    };

    const mapCouponPropsToOption = coupon => ({
        value: coupon.id,
        icon: null,
        text: coupon.title,
        onClick: () => onClickCoupon(coupon),
    });

    const mapMenu = useMemo(
        () =>
            context?.locationId
                ? getCouponsOptionsByLocationsId(context.locationId, mapCouponPropsToOption)
                : getCouponsOptionsByBrandId(context?.brandId, mapCouponPropsToOption),
        [context?.locationId, context?.brandId, getCouponsOptionsByLocationsId, getCouponsOptionsByBrandId],
    );

    return (
        <>
            <MenuList
                options={mapMenu}
                anchorEl={anchorElMain}
                onCloseMenu={handleCloseMain}
                openModal={isMobileMenuOpen}
                handleCloseModal={() => setMobileMenuOpen(false)}
                mainTitle={t('Sidebar.coupons')}
                menuButton={
                    <Tooltip title={t('Sidebar.coupons')} placement="top">
                        <IconButton
                            size="small"
                            color="inherit"
                            onClick={handleOpenMain}
                            disabled={disabled}
                            aria-label={t('Sidebar.coupons')}
                            sx={{
                                height: 18,
                                width: 18,
                                padding: '2px !important',
                                '& svg': { color: 'var(--text-secondary)', fontSize: 16 },
                            }}
                        >
                            <LocalActivityRoundedIcon />
                        </IconButton>
                    </Tooltip>
                }
            />
        </>
    );
};

export default CouponsMenu;
